import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface PinStatusPayload {
  enabled: boolean;
  validated?: boolean;
  attemptsRemaining?: number;
  lockedUntil?: string;
  pinUrl?: string;
  helloUrl?: string;
  message?: string;
}

interface PinSubmitPayload {
  success: boolean;
  remainingAttempts?: number;
  lockedUntil?: string;
  redirectUrl?: string;
  error?: string;
  pinSessionToken?: string;
}

interface PinState {
  isLoading: boolean;
  isSubmitting: boolean;
  isPinRequired: boolean;
  isPinValidated: boolean;
  attemptsRemaining: number | null;
  lockedUntil: string | null;
  error: string | null;
  pinUrl: string;
  helloUrl: string;
  lastChecked: number | null;
  sessionToken: string | null;
}

interface SubmitPinOptions {
  pin: string;
  signal?: AbortSignal;
}

interface SubmitPinResult {
  success: boolean;
  redirectUrl?: string;
}

interface PinContextType extends PinState {
  refreshStatus: (signal?: AbortSignal) => Promise<void>;
  submitPin: (options: SubmitPinOptions) => Promise<SubmitPinResult>;
  clearPinSession: (signal?: AbortSignal) => Promise<void>;
  setErrorMessage: (error: string | null) => void;
}

const DEFAULT_PIN_URL = '/pin';
const DEFAULT_HELLO_URL = '/hello';

const PinContext = createContext<PinContextType | null>(null);

// API is served same-origin via Pages Functions (/api/*).
const API_BASE: string = import.meta.env.VITE_API_BASE_URL || '';
export const PIN_SESSION_STORAGE_KEY = 'pin_session_token';

/**
 * PIN session storage lives in sessionStorage: the HttpOnly cookie is the
 * real credential (same-origin API); this copy only feeds the X-Pin-Session
 * header on API calls. sessionStorage dies with the tab — closing the tab
 * requires re-entering the PIN, per security review.
 */
const pinStorage = {
  get(): string | null {
    try { return window.sessionStorage.getItem(PIN_SESSION_STORAGE_KEY); } catch { return null; }
  },
  set(token: string) {
    try { window.sessionStorage.setItem(PIN_SESSION_STORAGE_KEY, token); } catch { /* private mode */ }
  },
  remove() {
    try { window.sessionStorage.removeItem(PIN_SESSION_STORAGE_KEY); } catch { /* noop */ }
    // Clear any legacy token from the localStorage era
    try { pinStorage.remove(); } catch { /* noop */ }
  },
};

const baseState: Omit<PinState, 'sessionToken'> = {
  isLoading: true,
  isSubmitting: false,
  isPinRequired: false,
  isPinValidated: false,
  attemptsRemaining: null,
  lockedUntil: null,
  error: null,
  pinUrl: DEFAULT_PIN_URL,
  helloUrl: DEFAULT_HELLO_URL,
  lastChecked: null,
};
// Migrate away the localStorage-era JWT credential (HttpOnly cookie is now
// authoritative). One-time cleanup for returning visitors.
if (typeof window !== 'undefined') {
  try { window.localStorage.removeItem('jwt_token'); } catch { /* noop */ }
}

const initialState: PinState = {
  ...baseState,
  sessionToken:
    typeof window !== 'undefined'
      ? pinStorage.get()
      : null,
};

async function parseJsonSafe<T>(response: Response): Promise<T | null> {
  try {
    return await response.json();
  } catch (error) {
    console.warn('Failed to parse JSON response for PIN gate request', error);
    return null;
  }
}

export const PinProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<PinState>(initialState);

  const updateState = useCallback((updates: Partial<PinState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const setErrorMessage = useCallback((errorMessage: string | null) => {
    updateState({ error: errorMessage });
  }, [updateState]);

  const refreshStatus = useCallback(
    async (signal?: AbortSignal) => {
      updateState({ isLoading: true, error: null });

      try {
        const token =
          state.sessionToken ??
          (typeof window !== 'undefined'
            ? pinStorage.get()
            : null);
        const headers = new Headers({
          Accept: 'application/json',
        });
        if (token) {
          headers.set('X-Pin-Session', token);
        }

        const response = await fetch(`${API_BASE}/api/auth/pin-status`, {
          method: 'GET',
          credentials: 'include',
          headers,
          signal,
        });

        if (response.status === 404) {
          updateState({
            ...baseState,
            isLoading: false,
            isPinRequired: false,
            isPinValidated: true,
            lastChecked: Date.now(),
            sessionToken: null,
          });
          return;
        }

        const payload = (await parseJsonSafe<PinStatusPayload>(
          response,
        )) ?? { enabled: false };
        const validated =
          Boolean(payload.validated) || !payload.enabled;
        if (!validated && typeof window !== 'undefined') {
          pinStorage.remove();
        }

        updateState({
          isLoading: false,
          isPinRequired: Boolean(payload.enabled),
          isPinValidated: validated,
          attemptsRemaining:
            typeof payload.attemptsRemaining === 'number'
              ? payload.attemptsRemaining
              : null,
          lockedUntil: payload.lockedUntil ?? null,
          error: null,
          pinUrl: payload.pinUrl || DEFAULT_PIN_URL,
          helloUrl: payload.helloUrl || DEFAULT_HELLO_URL,
          lastChecked: Date.now(),
          sessionToken:
            validated && token
              ? token
              : validated
              ? state.sessionToken
              : null,
        });
      } catch (error) {
        console.error('Failed to refresh PIN status', error);
        updateState({
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to load PIN status',
    });
      }
    },
    [state.sessionToken, updateState],
  );

  const submitPin = useCallback(
    async ({ pin, signal }: SubmitPinOptions): Promise<SubmitPinResult> => {
      if (!pin || pin.length !== 6) {
        updateState({ error: 'PIN must be exactly 6 digits long.' });
        return { success: false, redirectUrl: state.helloUrl };
      }

      updateState({ isSubmitting: true, error: null });

      try {
        const headers = new Headers({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        });
        const token =
          state.sessionToken ??
          (typeof window !== 'undefined'
            ? pinStorage.get()
            : null);
        if (token) {
          headers.set('X-Pin-Session', token);
        }

        const response = await fetch(`${API_BASE}/api/auth/pin`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({ pin }),
          signal,
        });

        const payload =
          (await parseJsonSafe<PinSubmitPayload>(response)) ?? {
            success: false,
          };

        if (!response.ok || !payload.success) {
          updateState({
            isSubmitting: false,
            isPinValidated: false,
            attemptsRemaining:
              typeof payload.remainingAttempts === 'number'
                ? payload.remainingAttempts
                : state.attemptsRemaining,
            lockedUntil: payload.lockedUntil ?? state.lockedUntil,
            error:
              response.status === 429
                ? 'Too many attempts. Please try again later.'
                : null,
          });

          return {
            success: false,
            redirectUrl: payload.redirectUrl || state.helloUrl,
          };
        }

        updateState({
          isSubmitting: false,
          isPinValidated: true,
          attemptsRemaining:
            typeof payload.remainingAttempts === 'number'
              ? payload.remainingAttempts
              : null,
          lockedUntil: payload.lockedUntil ?? null,
          error: null,
        });

        const nextToken =
          payload.pinSessionToken ||
          state.sessionToken ||
          (typeof window !== 'undefined'
            ? pinStorage.get()
            : null) ||
          null;
        if (payload.pinSessionToken && typeof window !== 'undefined') {
          pinStorage.set(payload.pinSessionToken);
        }
        updateState({
          sessionToken: nextToken,
        });

        return {
          success: true,
          redirectUrl: payload.redirectUrl || '/',
        };
      } catch (error) {
        console.error('PIN submission failed', error);
        updateState({
          isSubmitting: false,
          error:
            error instanceof Error
              ? error.message
              : 'Unable to verify PIN at this time.',
        });
        return { success: false, redirectUrl: state.helloUrl };
      }
    },
    [
      state.attemptsRemaining,
      state.helloUrl,
      state.lockedUntil,
      state.sessionToken,
      updateState,
    ],
  );

  const clearPinSession = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const token =
          state.sessionToken ??
          (typeof window !== 'undefined'
            ? pinStorage.get()
            : null);
        const headers = new Headers({
          Accept: 'application/json',
        });
        if (token) {
          headers.set('X-Pin-Session', token);
        }

        await fetch(`${API_BASE}/api/auth/pin/logout`, {
          method: 'POST',
          credentials: 'include',
          headers,
          signal,
        });
      } catch (error) {
        console.warn('Failed to clear PIN session', error);
      } finally {
        if (typeof window !== 'undefined') {
          pinStorage.remove();
        }
        updateState({
          isPinValidated: false,
          isPinRequired: state.isPinRequired,
          sessionToken: null,
        });
      }
    },
    [state.isPinRequired, state.sessionToken, updateState],
  );

  useEffect(() => {
    const controller = new AbortController();
    refreshStatus(controller.signal);
    return () => controller.abort();
  }, [refreshStatus]);

  const contextValue: PinContextType = useMemo(
    () => ({
      ...state,
      refreshStatus,
      submitPin,
      clearPinSession,
      setErrorMessage,
    }),
    [state, refreshStatus, submitPin, clearPinSession, setErrorMessage],
  );

  return (
    <PinContext.Provider value={contextValue}>
      {children}
    </PinContext.Provider>
  );
};

export const usePinGate = (): PinContextType => {
  const context = useContext(PinContext);
  if (!context) {
    throw new Error('usePinGate must be used within a PinProvider');
  }

  return context;
};
